import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixSocialGroupsApiV2GroupsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/groups-proxy',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/social-groups-proxy/groups',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/social-groups-proxy/groups',
        destPath: '',
      },
      {
        srcPath: '/_api/groups-proxy',
        destPath: '',
      },
    ],
    'editor-flow.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/groups',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/groups',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/groups',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/social-groups-proxy/groups',
        destPath: '',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/groups',
        destPath: '',
      },
    ],
    'groups.wixapps.net': [
      {
        srcPath: '/_api/social-groups-proxy/groups',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/groups',
        destPath: '',
      },
      {
        srcPath: '/social-groups-proxy/api/groups',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/social-groups-proxy/groups',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/social-groups-proxy/groups',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_groups_groups';

/**
 * Creates a group.
 *
 * When a group is created, the newly created group is added to the [Groups List](https://support.wix.com/en/article/wix-groups-about-your-groups-pages#groups-group-list) page
 * of a site.
 *
 * Specify a `creatorId` to set the group's creator. The group's creator will automatically become a group admin. See [Terminology](https://dev.wix.com/docs/rest/crm/community/groups/terminology) for more information on roles.
 *
 *
 * Wix users determine who can create a group.
 * This setting can be found in [a site's Dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Fwix-groups/settings?) under **Groups > General Settings > Group Creation**.
 * If set to **members with approval**, site members can create a group with the Create Group method, and the group becomes a `createRequest` with a status of `PENDING`.
 * A Wix user either approves or rejects the request to create a group.
 * If set to **all site members**, site members can create a group with the Create Group method and no approval is required.
 * If set to **only admins**, only Wix users can create a group with the Create Group method.
 * Default is set to **members with approval**.
 */
export function createGroup(payload: object): RequestOptionsFactory<any> {
  function __createGroup({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'group.createdDate' },
          { path: 'group.updatedDate' },
          { path: 'group.recentActivityDate' },
          { path: 'group.lastActivityDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'group.details.logoPosition.x' },
          { path: 'group.details.logoPosition.y' },
          { path: 'group.details.mobileLogoPosition.x' },
          { path: 'group.details.mobileLogoPosition.y' },
          { path: 'group.coverImage.position.x' },
          { path: 'group.coverImage.position.y' },
          { path: 'group.coverImage.mobilePosition.x' },
          { path: 'group.coverImage.mobilePosition.y' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group',
      method: 'POST' as any,
      methodFqn: 'wix.social.groups.api.v2.GroupsService.CreateGroup',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupsServiceUrl({
        protoPath: '/v2/groups',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'group.createdDate' },
              { path: 'group.updatedDate' },
              { path: 'group.recentActivityDate' },
              { path: 'group.lastActivityDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'group.details.logoPosition.x' },
              { path: 'group.details.logoPosition.y' },
              { path: 'group.details.mobileLogoPosition.x' },
              { path: 'group.details.mobileLogoPosition.y' },
              { path: 'group.coverImage.position.x' },
              { path: 'group.coverImage.position.y' },
              { path: 'group.coverImage.mobilePosition.x' },
              { path: 'group.coverImage.mobilePosition.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createGroup;
}

/**
 * Updates a group.
 *
 * When a public or private group's name is updated, the slug is updated to reflect the new group name.
 * Only group admins can update their group.
 *
 * > **Notes:**
 * > + When `group.privacyStatus` is updated from `PRIVATE` to `PUBLIC`, all pending group join requests are automatically approved.
 * > + When `group.privacyStatus` is updated from `PRIVATE` to `SECRET`, all pending group join requests are automatically rejected.
 */
export function updateGroup(payload: object): RequestOptionsFactory<any> {
  function __updateGroup({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'group.createdDate' },
          { path: 'group.updatedDate' },
          { path: 'group.recentActivityDate' },
          { path: 'group.lastActivityDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'group.details.logoPosition.x' },
          { path: 'group.details.logoPosition.y' },
          { path: 'group.details.mobileLogoPosition.x' },
          { path: 'group.details.mobileLogoPosition.y' },
          { path: 'group.coverImage.position.x' },
          { path: 'group.coverImage.position.y' },
          { path: 'group.coverImage.mobilePosition.x' },
          { path: 'group.coverImage.mobilePosition.y' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group',
      method: 'PATCH' as any,
      methodFqn: 'wix.social.groups.api.v2.GroupsService.UpdateGroup',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupsServiceUrl({
        protoPath: '/v2/groups/{group.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'group.createdDate' },
              { path: 'group.updatedDate' },
              { path: 'group.recentActivityDate' },
              { path: 'group.lastActivityDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'group.details.logoPosition.x' },
              { path: 'group.details.logoPosition.y' },
              { path: 'group.details.mobileLogoPosition.x' },
              { path: 'group.details.mobileLogoPosition.y' },
              { path: 'group.coverImage.position.x' },
              { path: 'group.coverImage.position.y' },
              { path: 'group.coverImage.mobilePosition.x' },
              { path: 'group.coverImage.mobilePosition.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateGroup;
}

export function deleteGroup(payload: object): RequestOptionsFactory<any> {
  function __deleteGroup({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group',
      method: 'DELETE' as any,
      methodFqn: 'wix.social.groups.api.v2.GroupsService.DeleteGroup',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupsServiceUrl({
        protoPath: '/v2/groups/{groupId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'group.createdDate' },
              { path: 'group.updatedDate' },
              { path: 'group.recentActivityDate' },
              { path: 'group.lastActivityDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'group.details.logoPosition.x' },
              { path: 'group.details.logoPosition.y' },
              { path: 'group.details.mobileLogoPosition.x' },
              { path: 'group.details.mobileLogoPosition.y' },
              { path: 'group.coverImage.position.x' },
              { path: 'group.coverImage.position.y' },
              { path: 'group.coverImage.mobilePosition.x' },
              { path: 'group.coverImage.mobilePosition.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __deleteGroup;
}

/**
 * Retrieves a group.
 *
 * For groups with `group.privacyStatus` set to `SECRET`, only group admins and group members can see the group and its content.
 */
export function getGroup(payload: object): RequestOptionsFactory<any> {
  function __getGroup({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group',
      method: 'GET' as any,
      methodFqn: 'wix.social.groups.api.v2.GroupsService.GetGroup',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupsServiceUrl({
        protoPath: '/v2/groups/{groupId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'group.createdDate' },
              { path: 'group.updatedDate' },
              { path: 'group.recentActivityDate' },
              { path: 'group.lastActivityDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'group.details.logoPosition.x' },
              { path: 'group.details.logoPosition.y' },
              { path: 'group.details.mobileLogoPosition.x' },
              { path: 'group.details.mobileLogoPosition.y' },
              { path: 'group.coverImage.position.x' },
              { path: 'group.coverImage.position.y' },
              { path: 'group.coverImage.mobilePosition.x' },
              { path: 'group.coverImage.mobilePosition.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getGroup;
}

/**
 * Retrieves a group by slug.
 *
 * The slug is the end of a group's URL that refers to a specific group.
 * For example, if a group's URL is `https:/example.com/groups/{my-fitness-group}`, the slug is `my-fitness-group`.
 * Slugs are case-sensitive. It is generally based on the group name, but for secret groups it is an autogenerated string of characters,
 * for example, `https:/example.com/groups/{5D3yTX}`.
 *
 * For groups with `group.privacyStatus` set to `SECRET`, only group admins and group members can see the group and its content.
 */
export function getGroupBySlug(payload: object): RequestOptionsFactory<any> {
  function __getGroupBySlug({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group',
      method: 'GET' as any,
      methodFqn: 'wix.social.groups.api.v2.GroupsService.GetGroupBySlug',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupsServiceUrl({
        protoPath: '/v2/groups/slugs/{slug}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'group.createdDate' },
              { path: 'group.updatedDate' },
              { path: 'group.recentActivityDate' },
              { path: 'group.lastActivityDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'group.details.logoPosition.x' },
              { path: 'group.details.logoPosition.y' },
              { path: 'group.details.mobileLogoPosition.x' },
              { path: 'group.details.mobileLogoPosition.y' },
              { path: 'group.coverImage.position.x' },
              { path: 'group.coverImage.position.y' },
              { path: 'group.coverImage.mobilePosition.x' },
              { path: 'group.coverImage.mobilePosition.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getGroupBySlug;
}

/**
 * Retrieves up to 100 groups.
 *
 *
 * Default sorts by `_createdDate` in descending order. For `SECRET` groups, only group admins and group members can see a list of groups and their content.
 *
 * > **Note:** This function's parameters are positional, and must be specified in the sequence shown in the syntax below. When specifying a parameter, use `null` as a placeholder for any unspecified parameters. For example, to specify limit only, call `listGroups(paging, null)`.
 */
export function listGroups(payload: object): RequestOptionsFactory<any> {
  function __listGroups({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group',
      method: 'GET' as any,
      methodFqn: 'wix.social.groups.api.v2.GroupsService.ListGroups',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupsServiceUrl({
        protoPath: '/v2/groups',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'groups.createdDate' },
              { path: 'groups.updatedDate' },
              { path: 'groups.recentActivityDate' },
              { path: 'groups.lastActivityDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'groups.details.logoPosition.x' },
              { path: 'groups.details.logoPosition.y' },
              { path: 'groups.details.mobileLogoPosition.x' },
              { path: 'groups.details.mobileLogoPosition.y' },
              { path: 'groups.coverImage.position.x' },
              { path: 'groups.coverImage.position.y' },
              { path: 'groups.coverImage.mobilePosition.x' },
              { path: 'groups.coverImage.mobilePosition.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listGroups;
}

/**
 * Retrieves up to 100 groups, given the provided paging, filtering, and sorting.
 *
 * Supported fields for filtering:
 * - `title`
 *
 * Supported fields for sorting:
 * - `title`
 * - `createdDate`
 * - `membersCount`
 * - `recentActivityDate`
 *
 * For groups with `group.privacyStatus` set to `SECRET`, only group admins and group members can see the group and its content.
 */
export function queryGroups(payload: object): RequestOptionsFactory<any> {
  function __queryGroups({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.social_groups.v2.group',
      method: 'POST' as any,
      methodFqn: 'wix.social.groups.api.v2.GroupsService.QueryGroups',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixSocialGroupsApiV2GroupsServiceUrl({
        protoPath: '/v2/groups/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'groups.createdDate' },
              { path: 'groups.updatedDate' },
              { path: 'groups.recentActivityDate' },
              { path: 'groups.lastActivityDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'groups.details.logoPosition.x' },
              { path: 'groups.details.logoPosition.y' },
              { path: 'groups.details.mobileLogoPosition.x' },
              { path: 'groups.details.mobileLogoPosition.y' },
              { path: 'groups.coverImage.position.x' },
              { path: 'groups.coverImage.position.y' },
              { path: 'groups.coverImage.mobilePosition.x' },
              { path: 'groups.coverImage.mobilePosition.y' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryGroups;
}
